import { useContext, useEffect, useReducer, useState } from 'react'
import { toast } from 'react-toastify'

import {
  Box,
  Chip,
  Grid,
  IconButton,
  Tab,
  Tabs,
  Typography,
  useTheme
} from '@material-ui/core'
import Button from '@material-ui/core/Button'
import Paper from '@material-ui/core/Paper'

import MainContainer from '../../components/MainContainer'
import MainHeader from '../../components/MainHeader'
import MainHeaderButtonsWrapper from '../../components/MainHeaderButtonsWrapper'

import ConfirmationModal from '../../components/ConfirmationModal'
import ProtectedRoute from '../../components/ProtectedRoute'
import UserModal from '../../components/UserModal'
import UserRolesModal from '../../components/UserRolesModal'
import { SocketContext } from '../../context/Socket/SocketContext'
import toastError from '../../errors/toastError'
import api from '../../services/api'
import { i18n } from '../../translate/i18n'

import {
  Clock,
  Plus,
  UserRoundPlusIcon
} from 'lucide-react'
import { ButtonAddNew } from '../../components/ButtonAddNew'
import DepartmentUserScheduleModal from '../../components/DepartmentUserScheduleModal'
import { InputSearch } from '../../components/InputSearch'
import QuickTable from '../../components/QuickTable'
import { TablePaginationFooter } from '../../components/TablePaginationFooter'
import { i18nToast } from '../../helpers/i18nToast'
import { useStyles } from './style'

function TabPanel(props) {
  const { children, value, index, ...other } = props
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`user-tabpanel-${index}`}
      aria-labelledby={`user-tab-${index}`}
      {...other}
    >
      {value === index && <Box style={{ paddingTop: 16 }}>{children}</Box>}
    </div>
  )
}

const reducer = (state, action) => {
  if (action.type === 'LOAD_USERS') {
    const users = action.payload
    const newUsers = []

    users.forEach(user => {
      const userIndex = state.findIndex(u => u.id === user.id)
      if (userIndex !== -1) {
        state[userIndex] = user
      } else {
        newUsers.push(user)
      }
    })

    return [...state, ...newUsers]
  }

  if (action.type === 'LOAD_MORE_USERS') {
    const users = action.payload
    // Para "load more", simplesmente adiciona os novos usuários ao final da lista
    return [...state, ...users]
  }

  if (action.type === 'UPDATE_USERS') {
    const user = action.payload
    const userIndex = state.findIndex(u => u.id === user.id)

    if (userIndex !== -1) {
      state[userIndex] = user
      return [...state]
    } else {
      return [user, ...state]
    }
  }

  if (action.type === 'DELETE_USER') {
    const userId = action.payload
    return state.filter(u => u.id !== userId)
  }

  if (action.type === 'RESET') {
    return []
  }
}

const Users = () => {
  const classes = useStyles()
  const theme = useTheme()

  const [tabValue, setTabValue] = useState(0)
  const [loading, setLoading] = useState(false)
  const [loadingMore, setLoadingMore] = useState(false)
  const [pageNumber, setPageNumber] = useState(1)
  const [hasMore, setHasMore] = useState(false)
  const [selectedUser, setSelectedUser] = useState(null)
  const [deletingUser, setDeletingUser] = useState(null)
  const [userModalOpen, setUserModalOpen] = useState(false)
  const [rolesModalOpen, setRolesModalOpen] = useState(false)
  const [confirmModalOpen, setConfirmModalOpen] = useState(false)
  const [searchParam, setSearchParam] = useState('')
  const [users, dispatch] = useReducer(reducer, [])
  const [isRefreshing, setIsRefreshing] = useState(false)

  const socketManager = useContext(SocketContext)

  const [userScheduleModalOpen, setUserScheduleModalOpen] = useState(false)
  const [savedSchedules, setSavedSchedules] = useState([])
  const [selectedScheduleId, setSelectedScheduleId] = useState(null)

  const [rowsPerPage, setRowsPerPage] = useState(25)
  const [totalUsers, setTotalUsers] = useState(0)

  useEffect(() => {
    dispatch({ type: 'RESET' })
    setPageNumber(1)
  }, [searchParam, rowsPerPage])

  // useEffect(() => {
  //   if (isRefreshing || tabValue !== 0) return

  //   if (pageNumber === 1) {
  //     setLoading(true)
  //   } else {
  //     setLoadingMore(true)
  //   }

  //   const delayDebounceFn = setTimeout(() => {
  //     const fetchUsers = async () => {
  //       try {
  //         const { data } = await api.get('/users/', {
  //           params: { searchParam, pageNumber }
  //         })

  //         const usersWithRoles = await Promise.all(
  //           data.users.map(async user => {
  //             try {
  //               // Buscamos as roles de TODOS os usuários de forma homogênea
  //               const { data: roleUsers } = await api.get(`/role/${user.id}`)
  //               const userRoles = Array.isArray(roleUsers) ? roleUsers : []

  //               return {
  //                 ...user,
  //                 roles: userRoles // Injeta o array real de permissões
  //               }
  //             } catch (roleErr) {
  //               console.error(
  //                 `Erro ao buscar roles do usuário ${user.id}:`,
  //                 roleErr
  //               )
  //               return { ...user, roles: [] }
  //             }
  //           })
  //         )

  //         if (pageNumber === 1) {
  //           dispatch({ type: 'LOAD_USERS', payload: usersWithRoles })
  //         } else {
  //           dispatch({ type: 'LOAD_MORE_USERS', payload: usersWithRoles })
  //         }

  //         setHasMore(data.hasMore)
  //       } catch (err) {
  //         toastError(err)
  //       } finally {
  //         setLoading(false)
  //         setLoadingMore(false)
  //       }
  //     }
  //     fetchUsers()
  //   }, 500)
  //   return () => clearTimeout(delayDebounceFn)
  // }, [searchParam, pageNumber, isRefreshing, tabValue])

  useEffect(() => {
    if (isRefreshing || tabValue !== 0) return

    setLoading(true)

    const delayDebounceFn = setTimeout(() => {
      const fetchUsers = async () => {
        try {
          const { data } = await api.get('/users/', {
            params: { searchParam, pageNumber, limit: rowsPerPage }
          })

          console.log('Dados', data)

          const usersWithRoles = await Promise.all(
            data.users.map(async user => {
              try {
                const { data: roleUsers } = await api.get(`/role/${user.id}`)
                return {
                  ...user,
                  roles: Array.isArray(roleUsers) ? roleUsers : []
                }
              } catch (roleErr) {
                return { ...user, roles: [] }
              }
            })
          )

          dispatch({ type: 'RESET' })
          dispatch({ type: 'LOAD_USERS', payload: usersWithRoles })

          setHasMore(data.hasMore)
          setTotalUsers(data.count || 0)
        } catch (err) {
          toastError(err)
        } finally {
          setLoading(false)
        }
      }
      fetchUsers()
    }, 500)
    return () => clearTimeout(delayDebounceFn)
  }, [searchParam, pageNumber, rowsPerPage, isRefreshing, tabValue])

  useEffect(() => {
    if (tabValue === 1) {
      fetchSavedSchedules()
    }
  }, [tabValue])

  useEffect(() => {
    const companyId = localStorage.getItem('companyId')
    const socket = socketManager.GetSocket(companyId)

    const onCompanyUser = data => {
      if (data.action === 'update' || data.action === 'create') {
        dispatch({ type: 'UPDATE_USERS', payload: data.user })
      }

      if (data.action === 'delete') {
        dispatch({ type: 'DELETE_USER', payload: +data.userId })
      }
    }

    socket.on(`company-${companyId}-user`, onCompanyUser)

    return () => {
      socket.disconnect()
    }
  }, [socketManager])

  const handleChangePage = (event, newPage) => {
    setPageNumber(newPage)
  }

  const handleChangeRowsPerPage = event => {
    setRowsPerPage(parseInt(event.target.value, 10))
    setPageNumber(1)
  }

  const fetchSavedSchedules = async () => {
    try {
      const { data } = await api.get('/users/work-schedule-templates')
      setSavedSchedules(Array.isArray(data) ? data : [])
    } catch {
      i18nToast.error('Não foi possível buscar os horários salvos.')
    }
  }

  // Função para atualizar a lista de usuários
  const refreshUserList = async () => {
    if (isRefreshing) return // Evita chamadas duplicadas

    try {
      setIsRefreshing(true)
      setLoading(true)

      const { data } = await api.get('/users/', {
        params: { searchParam, pageNumber: 1 }
      })

      const usersWithRoles = await Promise.all(
        data.users.map(async user => {
          try {
            const isSuper =
              user.super === true || user.super === 1 || user.super === '1'
            const isAdmin = user.profile === 'admin'

            if (isSuper || isAdmin) {
              return { ...user, roles: [] }
            }

            const { data: roleUsers } = await api.get(`/role/${user.id}`)
            return {
              ...user,
              roles: Array.isArray(roleUsers) ? roleUsers : []
            }
          } catch {
            return { ...user, roles: [] }
          }
        })
      )

      dispatch({ type: 'RESET' })
      dispatch({ type: 'LOAD_USERS', payload: usersWithRoles })
      setHasMore(data.hasMore)
      setPageNumber(1)
    } catch (err) {
      toastError(err)
    } finally {
      setLoading(false)
      setIsRefreshing(false)
    }
  }

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue)
  }

  const handleOpenUserModal = () => {
    setSelectedUser(null)
    setUserModalOpen(true)
  }

  const handleCloseUserModal = async (refresh = false) => {
    setSelectedUser(null)
    setUserModalOpen(false)

    // Sempre atualiza a lista de usuários quando o modal for fechado
    await refreshUserList()
  }

  const handleSearch = event => {
    setSearchParam(event.target.value.toLowerCase())
  }

  const handleEditUser = user => {
    setSelectedUser(user)
    setUserModalOpen(true)
  }

  // const handleManageRoles = user => {
  //   setSelectedUser(user)
  //   setRolesModalOpen(true)
  // }

  const handleDeleteUser = async userId => {
    try {
      await api.delete(`/users/${userId}`)
      toast.success(i18n.t('users.toasts.deleted'))
      dispatch({ type: 'DELETE_USER', payload: userId })
    } catch (err) {
      toastError(err)
    }
    setDeletingUser(null)
    setConfirmModalOpen(false)
  }

  const loadMore = () => {
    if (!loading && !loadingMore && hasMore) {
      setPageNumber(prevState => prevState + 1)
    }
  }

  const handleScroll = e => {
    if (!hasMore || loading || loadingMore) return
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget
    // Reduzindo a margem para 50px para detectar melhor quando está próximo do final
    if (scrollHeight - (scrollTop + 50) < clientHeight) {
      loadMore()
    }
  }

  // const handleProfileChange = async (user, isAdmin) => {
  //   try {
  //     if (isAdmin) {
  //       // Adicionar role admin
  //       await api.post('/role', {
  //         userId: user.id,
  //         role: 'admin'
  //       })
  //       toast.success(`Role admin adicionada ao usuário ${user.name}!`)
  //     } else {
  //       // Remover role admin
  //       await api.delete(`/role/${user.id}/admin`)
  //       toast.success(`Role admin removida do usuário ${user.name}!`)
  //     }

  //     // Atualizar o usuário localmente
  //     const updatedUser = {
  //       ...user,
  //       profile: isAdmin ? 'admin' : 'user'
  //     }
  //     dispatch({ type: 'UPDATE_USERS', payload: updatedUser })
  //   } catch (err) {
  //     toastError(err)
  //   }
  // }

  const handleProfileChange = async (user, isAdmin) => {
    try {
      const newProfile = isAdmin ? 'admin' : 'usuarios' // Ajuste para 'user' se for o padrão do seu BD

      // 1. Atualiza a coluna 'profile' principal (Isso dispara o Socket)
      await api.put(`/users/${user.id}`, {
        ...user,
        profile: newProfile
      })

      // 2. Atualiza a tabela auxiliar de Roles com Try/Catch silencioso
      try {
        if (isAdmin) {
          await api.post('/role', { userId: user.id, role: 'admin' })
        } else {
          await api.delete(`/role/${user.id}/admin`)
        }
      } catch (roleErr) {
        // Se der Erro 500 de UniqueConstraint no banco, nós apenas ignoramos
        // pois significa que a permissão já estava correta lá no banco de dados.
        console.warn(
          'Aviso ao sincronizar roles auxiliares:',
          roleErr.response?.data || roleErr.message
        )
      }

      // 3. Feedback unificado para o usuário
      if (isAdmin) {
        toast.success(`Privilégio de administrador concedido a ${user.name}!`)
      } else {
        toast.success(`Privilégio de administrador removido de ${user.name}!`)
      }

      // 4. Atualiza o estado local imediatamente para evitar o delay do socket
      const updatedUser = {
        ...user,
        profile: newProfile,
        roles: isAdmin
          ? [...(user.roles || []), { role: 'admin' }]
          : (user.roles || []).filter(
              r => r.role !== 'admin' && r.role !== 'super'
            )
      }

      dispatch({ type: 'UPDATE_USERS', payload: updatedUser })
    } catch (err) {
      // Aqui só cai se o PUT principal falhar, o que realmente é um erro crítico
      toastError(err)
    }
  }

  const columns = [
    {
      field: 'name',
      headerName: i18n.t('users.table.name'),
      align: 'left'
    },
    {
      field: 'email',
      headerName: i18n.t('users.table.email'),
      align: 'left'
    },
    {
      field: 'profile',
      headerName: i18n.t('users.table.profile'),
      align: 'left'
    },
    {
      field: 'ativo',
      headerName: 'Ativo',
      align: 'left'
    }
  ]

  const containerProps = {
    variant: 'outlined',
    // onScroll: handleScroll,
    className: classes.mainPaper
  }

  return (
    <ProtectedRoute
      requiredRoles={['super', 'admin', 'usuarios']}
      fallbackPath="/tickets"
    >
      <MainContainer>
        <ConfirmationModal
          title={
            deletingUser &&
            `${i18n.t('users.confirmationModal.deleteTitle')} ${
              deletingUser.name
            }?`
          }
          open={confirmModalOpen}
          onClose={() => setConfirmModalOpen(false)}
          onConfirm={() => handleDeleteUser(deletingUser.id)}
        >
          {i18n.t('users.confirmationModal.deleteMessage')}
        </ConfirmationModal>
        <UserModal
          open={userModalOpen}
          onClose={handleCloseUserModal}
          aria-labelledby="form-dialog-title"
          userId={selectedUser && selectedUser.id}
        />
        <UserRolesModal
          open={rolesModalOpen}
          onClose={() => {
            setRolesModalOpen(false)
            setSelectedUser(null)
          }}
          user={selectedUser}
        />
        <MainHeader
          title={i18n.t('users.title')}
          description="Gerencie os usuários do sistema, definindo permissões e acessos para cada membro da equipe."
          showTutorial
          // onTutorialClick={handleOpenTutorial}
        >
          <MainHeaderButtonsWrapper>
            <div className={classes.buttonContainer}>
              <ButtonAddNew
                onClick={handleOpenUserModal}
                startIcon={<UserRoundPlusIcon size={20} />}
                label="Novo Usuário"
              />
            </div>
          </MainHeaderButtonsWrapper>
        </MainHeader>

        <Grid container className={classes.boxForms}>
          <Grid item xs={2} className={classes.selectContainer}>
            <InputSearch
              value={searchParam}
              onChange={handleSearch}
              placeholder={'Pesquisar'}
            />
          </Grid>
        </Grid>

        <Paper
          className={classes.mainPaper}
          variant="outlined"
          // onScroll={handleScroll}
        >
          <Tabs
            value={tabValue}
            onChange={handleTabChange}
            indicatorColor="primary"
            textColor="primary"
            scrollButtons="on"
            variant="scrollable"
            className={classes.tab}
          >
            <Tab label="Usuários" className={classes.tabHeader} />
            <Tab label="Horários" className={classes.tabHeader} />
          </Tabs>

          <TabPanel value={tabValue} index={0}>
            <QuickTable
              data={users}
              columns={columns}
              loading={loading}
              actions={['edit', 'roles', 'delete']}
              onEdit={handleEditUser}
              // onRoles={handleManageRoles}
              onDelete={user => {
                setConfirmModalOpen(true)
                setDeletingUser(user)
              }}
              containerProps={containerProps}
              skeletonRows={4}
              emptyMessage={i18n.t(
                'users.table.noUsers',
                'Nenhum usuário encontrado'
              )}
              customCellRenderer={{
                profile: user => {
                  const hasRoles = user.roles && user.roles.length > 0

                  const isSuper =
                    hasRoles && user.roles.some(r => r.role === 'super')
                  const isAdmin =
                    hasRoles && user.roles.some(r => r.role === 'admin')
                  const isGestor =
                    hasRoles && user.roles.some(r => r.role === 'gestor')

                  // Inicializa como Usuário padrão (se o array de roles for vazio)
                  let chipLabel = 'Usuário'
                  let chipColor = theme.palette.blue_300.main

                  if (isSuper) {
                    chipLabel = 'Super'
                  } else if (isAdmin) {
                    chipLabel = 'Admin'
                  } else if (isGestor) {
                    chipLabel = 'Gestor'
                  }

                  return (
                    <Chip
                      label={chipLabel}
                      size="small"
                      style={{
                        backgroundColor: chipColor,
                        color: theme.palette.white_100.main,
                        fontWeight: 'bold',
                        borderRadius: '4px',
                        width: '85px',
                        textAlign: 'center',
                        height: '18px '
                      }}
                    />
                  )
                },
                ativo: user => {
                  const isActive = user.ativo !== false
                  return (
                    <Chip
                      label={isActive ? 'Ativo' : 'Bloqueado'}
                      size="small"
                      style={{
                        backgroundColor: isActive
                          ? theme.palette.blue_300.main
                          : theme.palette.red_100.main,
                        color: theme.palette.white_100.main,
                        fontWeight: 'bold',
                        borderRadius: '4px',
                        width: '90px',
                        height: '18px '
                      }}
                    />
                  )
                },
                super: user => {
                  const isSuper =
                    user.super === true ||
                    user.super === 1 ||
                    user.super === '1'

                  return (
                    <Chip
                      label={isSuper ? 'Sim' : 'Nao'}
                      size="small"
                      style={{
                        backgroundColor: isSuper
                          ? theme.palette.green?.main || '#2e7d32'
                          : theme.palette.grey[500],
                        color: theme.palette.white_100.main,
                        fontWeight: 'bold',
                        borderRadius: '8px',
                        width: '70px'
                      }}
                    />
                  )
                }
              }}
            />
            <TablePaginationFooter
              pageNumber={pageNumber}
              rowsPerPage={rowsPerPage}
              totalItems={totalUsers}
              loading={loading}
              onChangePage={setPageNumber}
              onChangeRowsPerPage={setRowsPerPage}
              title="usuários"
            />
          </TabPanel>

          <TabPanel value={tabValue} index={1}>
            <Box p={2}>
              <Typography variant="h6" className={classes.groupTitle}>
                Expediente dos Usuários
              </Typography>
              <Grid container spacing={2} className={classes.queueContainer}>
                {savedSchedules.map(schedule => (
                  <div key={schedule.id} className={classes.queueItem}>
                    <span>{schedule.name}</span>
                    <IconButton
                      className={classes.iconButton}
                      onClick={() => {
                        setSelectedScheduleId(schedule.id)
                        setUserScheduleModalOpen(true)
                      }}
                    >
                      <Clock size={16} />
                    </IconButton>
                  </div>
                ))}

                <Button
                  color="primary"
                  // variant="outlined"
                  onClick={() => {
                    setSelectedScheduleId(null)
                    setUserScheduleModalOpen(true)
                  }}
                  className={classes.buttonAddNewHour}
                >
                  <span>
                    Novo Horário
                    <Plus size={20} />
                  </span>
                </Button>
              </Grid>
            </Box>
          </TabPanel>
        </Paper>

        <DepartmentUserScheduleModal
          open={userScheduleModalOpen}
          scheduleId={selectedScheduleId}
          onClose={() => setUserScheduleModalOpen(false)}
          onSave={fetchSavedSchedules}
        />
      </MainContainer>
    </ProtectedRoute>
  )
}

export default Users
